package songwritersnotebook.app.utils

import songwritersnotebook.app.data.Line
import songwritersnotebook.app.data.Section
import songwritersnotebook.app.data.Song
import com.benasher44.uuid.uuid4

object SongEditorUtils {

    // Returns the updated Song AND the ID of the new section to focus
    fun addSection(song: Song, sectionIndex: Int, lineIndex: Int): Pair<Song, String> {
        val sections = song.sections.toMutableList()
        val sectionToSplit = sections[sectionIndex]

        // Safety check
        if (lineIndex >= sectionToSplit.lines.size) return Pair(song, "")

        val topLines = sectionToSplit.lines.subList(0, lineIndex + 1)
        val bottomLines = sectionToSplit.lines.subList(lineIndex + 1, sectionToSplit.lines.size)

        val newId = uuid4().toString()
        val newSection: Section
        
        val lastPos = sectionToSplit.position
        val nextPos = sections.getOrNull(sectionIndex + 1)?.position
        val newSectionPos = FractionalIndexer.generateBetween(lastPos, nextPos)

        if (bottomLines.isEmpty()) {
            newSection = Section(id = newId, title = "New Section", lines = listOf(Line(text = "", sectionId = newId)), position = newSectionPos)
            sections.add(sectionIndex + 1, newSection)
        } else {
            sections[sectionIndex] = sectionToSplit.copy(lines = topLines)
            newSection = Section(id = newId, title = "New Section", lines = bottomLines.map { it.copy(sectionId = newId) }, position = newSectionPos)
            sections.add(sectionIndex + 1, newSection)
        }

        return Pair(song.copy(sections = sections), newSection.id)
    }

    fun appendSection(song: Song): Pair<Song, String> {
        val sections = song.sections.toMutableList()
        val lastPos = sections.lastOrNull()?.position
        val newPos = FractionalIndexer.generateBetween(lastPos, null)
        val newId = uuid4().toString()
        
        val newSection = Section(
            id = newId,
            title = "New Section",
            position = newPos,
            lines = listOf(Line(text = "", sectionId = newId, position = FractionalIndexer.generateBetween(null, null)))
        )
        
        sections.add(newSection)
        return Pair(song.copy(sections = sections), newId)
    }

    fun mergeSectionWithPrevious(song: Song, sectionIndex: Int): Song {
        if (sectionIndex <= 0) return song
        val sections = song.sections.toMutableList()
        val previousSection = sections[sectionIndex - 1]
        val currentSection = sections.removeAt(sectionIndex)

        val mergedLines = previousSection.lines.toMutableList().apply {
            // If the last line of previous section is empty and next section has content, remove the empty line
            if (lastOrNull()?.text?.isEmpty() == true && currentSection.lines.isNotEmpty()) {
                removeAt(lastIndex)
            }
            addAll(currentSection.lines)
        }
        sections[sectionIndex - 1] = previousSection.copy(lines = mergedLines)
        return song.copy(sections = sections)
    }

    fun mergeLineWithPrevious(song: Song, sectionIndex: Int, lineIndex: Int, textToAppend: String, mergeMedia: Boolean = false): Song {
        val sections = song.sections.toMutableList()
        val lines = sections[sectionIndex].lines.toMutableList()
        val currentLine = lines[lineIndex]
        val lineId = currentLine.id

        // Remove the current line
        lines.removeAt(lineIndex)

        val updatedRecordings = song.recordings.toMutableMap()
        
        if (lineIndex > 0) {
            // Merging into the line above within the same section
            var previousLine = lines[lineIndex - 1]
            val targetId = previousLine.id
            
            // Text
            previousLine = previousLine.copy(text = previousLine.text + textToAppend)
            
            // Media
            if (mergeMedia) {
                previousLine = previousLine.copy(tabs = previousLine.tabs + currentLine.tabs)
                val currentLineRecordings = updatedRecordings[lineId] ?: emptyList()
                if (currentLineRecordings.isNotEmpty()) {
                    updatedRecordings[targetId] = (updatedRecordings[targetId] ?: emptyList()) + currentLineRecordings
                }
            }
            
            updatedRecordings.remove(lineId)
            lines[lineIndex - 1] = previousLine
            sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)
        } else if (sectionIndex > 0) {
            // Merging into the last line of the PREVIOUS section
            val prevSectionLines = sections[sectionIndex - 1].lines.toMutableList()
            if (prevSectionLines.isNotEmpty()) {
                var previousLine = prevSectionLines.last()
                val targetId = previousLine.id
                
                // Text
                previousLine = previousLine.copy(text = previousLine.text + textToAppend)
                
                // Media
                if (mergeMedia) {
                    previousLine = previousLine.copy(tabs = previousLine.tabs + currentLine.tabs)
                    val currentLineRecordings = updatedRecordings[lineId] ?: emptyList()
                    if (currentLineRecordings.isNotEmpty()) {
                        updatedRecordings[targetId] = (updatedRecordings[targetId] ?: emptyList()) + currentLineRecordings
                    }
                }
                
                updatedRecordings.remove(lineId)
                prevSectionLines[prevSectionLines.lastIndex] = previousLine
                sections[sectionIndex - 1] = sections[sectionIndex - 1].copy(lines = prevSectionLines)

                if (lines.isEmpty()) {
                    val sectionId = sections[sectionIndex].id
                    sections.removeAt(sectionIndex)
                    updatedRecordings.remove(sectionId)
                } else {
                    sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)
                }
            }
        }
        return song.copy(sections = sections, recordings = updatedRecordings)
    }

    // Returns updated Song AND the new line ID for focus
    fun splitLine(song: Song, sectionIndex: Int, lineIndex: Int, textBeforeSplit: String, textAfterSplit: String): Pair<Song, String> {
        val sections = song.sections.toMutableList()
        val lines = sections[sectionIndex].lines.toMutableList()

        lines[lineIndex] = lines[lineIndex].copy(text = textBeforeSplit)

        val newLine = Line(text = textAfterSplit)
        lines.add(lineIndex + 1, newLine)
        sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)

        return Pair(song.copy(sections = sections), newLine.id)
    }

    fun deleteSection(song: Song, sectionIndex: Int): Song {
        if (song.sections.size <= 1) return song
        val sectionId = song.sections[sectionIndex].id
        val updatedSections = song.sections.toMutableList().apply { removeAt(sectionIndex) }
        val updatedRecordings = song.recordings.toMutableMap().apply { remove(sectionId) }
        return song.copy(sections = updatedSections, recordings = updatedRecordings)
    }

    fun deleteLine(song: Song, sectionIndex: Int, lineIndex: Int): Song {
        val sections = song.sections.toMutableList()
        val lines = sections[sectionIndex].lines.toMutableList()
        val lineId = lines[lineIndex].id

        if (lines.size <= 1 && sections.size > 1) {
            val sectionId = sections[sectionIndex].id
            sections.removeAt(sectionIndex)
            val updatedRecordings = song.recordings.toMutableMap().apply { 
                remove(sectionId)
                remove(lineId) 
            }
            return song.copy(sections = sections, recordings = updatedRecordings)
        } else if (lines.size > 1) {
            lines.removeAt(lineIndex)
            sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)
            val updatedRecordings = song.recordings.toMutableMap().apply { remove(lineId) }
            return song.copy(sections = sections, recordings = updatedRecordings)
        } else {
            // Don't delete the last line of the last section, just clear it
            lines[lineIndex] = lines[lineIndex].copy(text = "")
            sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)
            val updatedRecordings = song.recordings.toMutableMap().apply { remove(lineId) }
            return song.copy(sections = sections, recordings = updatedRecordings)
        }
    }

    fun moveLineUp(song: Song, sectionIndex: Int, lineIndex: Int): Song {
        val sections = song.sections.toMutableList()
        if (lineIndex > 0) {
            val lines = sections[sectionIndex].lines.toMutableList()
            var movedLine = lines.removeAt(lineIndex)
            val prevPos = lines.getOrNull(lineIndex - 2)?.position
            val nextPos = lines.getOrNull(lineIndex - 1)?.position
            movedLine = movedLine.copy(position = FractionalIndexer.generateBetween(prevPos, nextPos))
            lines.add(lineIndex - 1, movedLine)
            sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)
        } else if (sectionIndex > 0) {
            val currentLines = sections[sectionIndex].lines.toMutableList()
            var movedLine = currentLines.removeAt(lineIndex)
            if (currentLines.isEmpty()) sections.removeAt(sectionIndex) else sections[sectionIndex] = sections[sectionIndex].copy(lines = currentLines)

            val prevLines = sections[sectionIndex - 1].lines.toMutableList()
            val prevPos = prevLines.lastOrNull()?.position
            movedLine = movedLine.copy(
                sectionId = sections[sectionIndex - 1].id,
                position = FractionalIndexer.generateBetween(prevPos, null)
            )
            prevLines.add(movedLine)
            sections[sectionIndex - 1] = sections[sectionIndex - 1].copy(lines = prevLines)
        }
        return song.copy(sections = sections)
    }

    fun moveLineDown(song: Song, sectionIndex: Int, lineIndex: Int): Song {
        val sections = song.sections.toMutableList()
        val lines = sections[sectionIndex].lines.toMutableList()

        if (lineIndex < lines.size - 1) {
            var movedLine = lines.removeAt(lineIndex)
            val prevPos = lines.getOrNull(lineIndex)?.position
            val nextPos = lines.getOrNull(lineIndex + 1)?.position
            movedLine = movedLine.copy(position = FractionalIndexer.generateBetween(prevPos, nextPos))
            lines.add(lineIndex + 1, movedLine)
            sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)
        } else if (sectionIndex < sections.size - 1) {
            var movedLine = lines.removeAt(lineIndex)
            if (lines.isEmpty()) sections.removeAt(sectionIndex) else sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)

            val nextSectionIndex = if (lines.isEmpty()) sectionIndex else sectionIndex + 1
            val nextLines = sections[nextSectionIndex].lines.toMutableList()
            val nextPos = nextLines.firstOrNull()?.position
            movedLine = movedLine.copy(
                sectionId = sections[nextSectionIndex].id,
                position = FractionalIndexer.generateBetween(null, nextPos)
            )
            nextLines.add(0, movedLine)
            sections[nextSectionIndex] = sections[nextSectionIndex].copy(lines = nextLines)
        }
        return song.copy(sections = sections)
    }

    fun moveSectionUp(song: Song, sectionIndex: Int): Song {
        if (sectionIndex == 0) return song
        val sections = song.sections.toMutableList()
        var movedSection = sections.removeAt(sectionIndex)
        val prevPos = sections.getOrNull(sectionIndex - 2)?.position
        val nextPos = sections.getOrNull(sectionIndex - 1)?.position
        movedSection = movedSection.copy(position = FractionalIndexer.generateBetween(prevPos, nextPos))
        sections.add(sectionIndex - 1, movedSection)
        return song.copy(sections = sections)
    }

    fun moveSectionDown(song: Song, sectionIndex: Int): Song {
        val sections = song.sections.toMutableList()
        if (sectionIndex >= sections.size - 1) return song
        var movedSection = sections.removeAt(sectionIndex)
        val prevPos = sections.getOrNull(sectionIndex)?.position
        val nextPos = sections.getOrNull(sectionIndex + 1)?.position
        movedSection = movedSection.copy(position = FractionalIndexer.generateBetween(prevPos, nextPos))
        sections.add(sectionIndex + 1, movedSection)
        return song.copy(sections = sections)
    }

    fun duplicateSection(song: Song, sectionIndex: Int): Song {
        val sections = song.sections.toMutableList()
        val originalSection = sections.getOrNull(sectionIndex) ?: return song

        val newSection = originalSection.copy(
            id = uuid4().toString(),
            lines = originalSection.lines.map { it.copy(id = uuid4().toString()) }
        )
        sections.add(sectionIndex + 1, newSection)
        return song.copy(sections = sections)
    }

    fun duplicateLine(song: Song, sectionIndex: Int, lineIndex: Int): Song {
        val sections = song.sections.toMutableList()
        val lines = sections.getOrNull(sectionIndex)?.lines?.toMutableList() ?: return song

        val originalLine = lines.getOrNull(lineIndex) ?: return song
        val newLine = originalLine.copy(id = uuid4().toString())

        lines.add(lineIndex + 1, newLine)
        sections[sectionIndex] = sections[sectionIndex].copy(lines = lines)
        return song.copy(sections = sections)
    }

    fun collectUniqueInstructions(song: Song): Set<String> {
        val instructions = mutableSetOf<String>()
        val allTabs = song.sections.flatMap { it.tabs } + song.sections.flatMap { it.lines }.flatMap { it.tabs }
        allTabs.forEach { tab ->
            tab.columns.forEach { col ->
                if (col.instruction.isNotBlank()) instructions.add(col.instruction)
            }
        }
        return instructions
    }

    fun calculateGlobalFootnotes(song: Song): Map<String, Int> {
        val footnotes = mutableMapOf<String, Int>()
        val allTabs = song.sections.flatMap { it.tabs } + song.sections.flatMap { it.lines }.flatMap { it.tabs }
        var currentIndex = 1

        // Collect all potentially long instructions from the song to assign global IDs
        val seenInstructions = mutableSetOf<String>()
        
        allTabs.forEach { tab ->
            tab.columns.forEach { col ->
                val noteLen = col.notes.maxOfOrNull { if (it == "-" || it.isBlank()) 1 else it.length } ?: 1
                if (col.instruction.length > noteLen) {
                    if (!seenInstructions.contains(col.instruction)) {
                        seenInstructions.add(col.instruction)
                        footnotes[col.instruction] = currentIndex
                        currentIndex++
                    }
                }
            }
        }
        return footnotes
    }
}
